/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0.
 * Uses the Web Crypto API (available in all modern browsers).
 */

/**
 * Generates a cryptographically random code verifier string.
 * @param {number} length - Length of the verifier (43–128 per RFC 7636).
 * @returns {string} A random URL-safe string.
 */
export function generateCodeVerifier(length = 64) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Derives a code challenge from a code verifier using SHA-256.
 * @param {string} verifier - The code verifier string.
 * @returns {Promise<string>} The base64url-encoded SHA-256 hash.
 */
export async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Generates a cryptographically random state parameter.
 * @returns {string} A random URL-safe string.
 */
export function generateState() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Base64url-encodes a Uint8Array (no padding, URL-safe).
 * @param {Uint8Array} bytes
 * @returns {string}
 */
function base64UrlEncode(bytes) {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
